import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Delete from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useRevokeShiftApplication } from '~/hooks/api/useRevokeShiftApplication';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import { useQueryClient } from '@tanstack/react-query';
import messages from './messages';

type RevokePublisherShiftApplicationButtonProps = {
  turnId: number;
  shiftId: number;
  shiftApplicationId: number;
  publisherName: string;
};

const RevokePublisherShiftApplicationButton = (
  props: RevokePublisherShiftApplicationButtonProps
) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const [isDialogVisible, setDialogVisibility] = useState(false);
  const handleOpenDialog = () => setDialogVisibility(true);
  const handleCloseDialog = () => setDialogVisibility(false);
  const mutation = useRevokeShiftApplication({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.snackbarSuccess, {
          publisherName: props.publisherName,
        }),
      });
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.snackbarFailure, {
          publisherName: props.publisherName,
        }),
      });
    },
    onSettled() {
      handleCloseDialog();
      queryClient.invalidateQueries({ queryKey: ['turn', props.turnId] });
    },
  });
  return (
    <>
      <IconButton
        onClick={handleOpenDialog}
        aria-label={intl.formatMessage(messages.delete, {
          publisherName: props.publisherName,
        })}
      >
        <Delete />
      </IconButton>
      <Dialog
        open={isDialogVisible}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <FormattedMessage {...messages.dialogTitle} />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage {...messages.dialogDescription} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            <FormattedMessage {...messages.dialogCancel} />
          </Button>
          <Button
            onClick={() => {
              mutation.mutate({
                turnId: props.turnId,
                shiftId: props.shiftId,
                shiftApplicationId: props.shiftApplicationId,
              });
            }}
            autoFocus
            color="error"
            variant="contained"
          >
            <FormattedMessage
              {...messages.dialogConfirm}
              values={{ publisherName: props.publisherName }}
            />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RevokePublisherShiftApplicationButton;
